import type { CSSProperties } from 'react';
import { Globe, Lock, GripVertical } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { NotificationBadge } from '@/components/ui/notification-badge';
import type { TodoListResponse } from '@/lib/api';

function formatRelativeTime(dateString: string): string {
  const now = Date.now();
  const then = new Date(dateString).getTime();
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'Updated just now';
  if (diffMin < 60) return `Updated ${diffMin}m ago`;
  if (diffHr < 24) return `Updated ${diffHr}h ago`;
  if (diffDays < 30) return `Updated ${diffDays}d ago`;
  return `Updated ${new Date(dateString).toLocaleDateString()}`;
}

interface ListCardProps {
  list: TodoListResponse;
  onClick: () => void;
  /** Unread assignment notifications for items/sections in this list. */
  unreadCount?: number;
  /** When true, the card registers as a drag-sortable node and shows a grip
   * handle. Gated to owner/admin/self by the caller — reordering is a workspace
   * write. Requires an ancestor DndContext + SortableContext. */
  sortable?: boolean;
}

export function ListCard({ list, onClick, unreadCount = 0, sortable = false }: ListCardProps) {
  // Called unconditionally (hooks rule); `disabled` no-ops it for viewers. The
  // card is only rendered inside MyListsPage, which always provides a DndContext.
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: list.listId,
    disabled: !sortable,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className="group cursor-pointer hover:border-primary/30 hover:shadow-sm transition-all"
      role="button"
      tabIndex={0}
      aria-label={`Todo list: ${list.name}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onClick();
      }}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            {sortable && (
              <button
                type="button"
                className="cursor-grab touch-none text-muted-foreground/40 hover:text-muted-foreground -ml-1 shrink-0"
                aria-label="Drag to reorder"
                // Stop the grip's own click/keydown from bubbling to the card's
                // navigate handler; drag itself is pointer-driven via listeners.
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                {...attributes}
                {...listeners}
              >
                <GripVertical className="h-4 w-4" />
              </button>
            )}
            <h3 className="font-semibold truncate">{list.name}</h3>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <NotificationBadge
              count={unreadCount}
              aria-label={`${unreadCount} new ${unreadCount === 1 ? 'assignment' : 'assignments'}`}
            />
            {list.isPublic ? (
              <>
                <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                <Badge variant="secondary" className="text-xs">
                  Public
                </Badge>
              </>
            ) : (
              <Lock className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {list.description && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{list.description}</p>
        )}
        <p className="text-xs text-muted-foreground">{formatRelativeTime(list.updatedAt)}</p>
      </CardContent>
    </Card>
  );
}
